import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Users, ClipboardList, LogOut, Sun, Moon, Monitor } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import type { ThemePreference } from "../types";

const themeOptions: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

export default function UserMenu() {
  const { user, logout, isAdmin } = useAuth();
  const { theme, setTheme, isLoading: themeLoading } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  if (!user) return null;

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-lg hover:bg-primary-400 dark:hover:bg-dark-bg-tertiary transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="w-8 h-8 rounded-full"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary-400 dark:bg-dark-bg-tertiary flex items-center justify-center text-sm font-medium text-white">
            {initials}
          </div>
        )}
        <ChevronDown
          className={`w-4 h-4 text-white transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-dark-bg-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-border py-1 z-50">
          {/* User info */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-border">
            <p className="text-sm font-medium text-gray-900 dark:text-dark-text truncate">
              {user.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-dark-text-secondary truncate">
              {user.email}
            </p>
            <span
              className={`inline-flex items-center px-2 py-0.5 mt-2 text-xs font-medium rounded-full ${
                isAdmin
                  ? "bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300"
                  : "bg-gray-100 text-gray-600 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary"
              }`}
            >
              {isAdmin ? "Admin" : "Read-only"}
            </span>
          </div>

          {/* Theme selector */}
          <div className="py-2 border-b border-gray-100 dark:border-dark-border">
            <div className="px-4 py-1 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wide">
              Theme
            </div>
            <div className="flex px-3 py-1">
              {themeOptions.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  disabled={themeLoading}
                  title={label}
                  className={`
                    flex-1 flex items-center justify-center py-2 transition-colors
                    first:rounded-l-md last:rounded-r-md
                    ${
                      theme === value
                        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-border"
                    }
                    disabled:opacity-50
                  `}
                  aria-pressed={theme === value}
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            {/* Admin-only links */}
            {isAdmin && (
              <>
                <Link
                  to="/users"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                  onClick={() => setIsOpen(false)}
                >
                  <Users className="w-4 h-4" />
                  Manage Users
                </Link>
                <Link
                  to="/audit"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                  onClick={() => setIsOpen(false)}
                >
                  <ClipboardList className="w-4 h-4" />
                  Audit Log
                </Link>
                <div className="border-t border-gray-100 dark:border-dark-border my-1" />
              </>
            )}

            {/* Logout */}
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
